/*
    Copyright 2022-2025, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";
import { Alert, Box, CircularProgress, Stack } from "@mui/material";
import Modal from "@mui/material/Modal";
import Backdrop from "@mui/material/Backdrop";
import Fade from "@mui/material/Fade";

import { Typography } from "@mui/material";

import { TextField, Button } from "@mui/material";
import { Effect, ModalBase, Setter } from "../../utils/Interfaces";
import { delayedAction } from "../../utils/Functions";
import { useWindowDimensions } from "../../utils/Hooks";

export interface ConfirmDeleteModalProps extends ModalBase {
    confirmText: string;
    description: string;
    onDelete: Setter<Effect>;
}

const ConfirmDeleteModal = (props: ConfirmDeleteModalProps) => {
    const { confirmText, onDelete, open, handleClose, description } = props;

    const [enteredText, setEnteredText] = useState<string>("");
    const [processing, setProcessing] = useState<boolean>(false);

    const { width } = useWindowDimensions();

    function close() {
        handleClose();
        void delayedAction(1000, () => {
            setProcessing(false);
            setEnteredText("");
        });
    }

    return (
        <Modal
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
            open={open}
            onClose={close}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 500 }}
        >
            <Fade in={open}>
                <Box
                    sx={{
                        backgroundColor: (theme) => theme.palette.background.paper,
                        border: "1px solid #000",
                        boxShadow: (theme) => theme.shadows[5],
                        padding: (theme) => theme.spacing(2, 4, 3),
                        zIndex: 1000,
                        maxWidth: width < 600 ? "100%" : width < 1000 ? "80%" : "60%",
                    }}
                >
                    <Alert severity="warning">
                        <Typography align="center" marginBottom={1}>
                            {description}
                        </Typography>
                    </Alert>
                    <Box sx={{ mb: 2 }} />
                    <Alert severity="error">
                        <Typography align="center" marginBottom={1}>
                            {`This cannot be undone, please type "${confirmText}" if you wish to proceed.`}
                        </Typography>
                    </Alert>

                    <TextField
                        onChange={(event) => setEnteredText(event.target.value)}
                        margin="normal"
                        required
                        fullWidth
                        id="confirm_deletion"
                        label={`${confirmText}`}
                        name="confirm_deletion"
                        value={enteredText}
                    />
                    <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
                        {processing && <CircularProgress size={20} sx={{ mt: 1, mb: 1 }} />}

                        {!processing && (
                            <>
                                <Button onClick={close} variant="contained" color="secondary">
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={() => {
                                        setProcessing(true);
                                        onDelete(close);
                                        setEnteredText("");
                                    }}
                                    disabled={enteredText !== confirmText}
                                >
                                    Delete
                                </Button>
                            </>
                        )}
                    </Stack>
                </Box>
            </Fade>
        </Modal>
    );
};

export default ConfirmDeleteModal;
